var _a;
import { ofOverrides } from "../../data/of-overrides.js";
import { optionsOverrides } from "../../data/options-overrides.js";
import { stringReplaceOverrides } from "../../data/string-replace-overrides.js";
import { superRegionDisplayNames } from "../../data/super-region-to-display-names.js";
/**
 *
 * @param code The display name code such as `en` for English or `US` for United States or EUR for Euro
 * @param locales A string with a BCP 47 language tag, or an array of such strings
 * @param options See Intl.DisplayNamesOptions. Defaults to { type: 'region' } or if no type provided will add type: region
 * @returns The display name requested. For more details see Intl.DisplayNames
 */
export const getDisplayName = (code, locales = undefined, options = {
    type: "region",
}) => {
    return new DisplayNames(locales, options).of(code);
};
const OriginalDisplayNames = (_a = Intl.DisplayNames) !== null && _a !== void 0 ? _a : function () { };
/**
 * Tesla wrapper around [`Intl.DisplayNames()`](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DisplayNames)
 * Adds functionality to allow for
 *   - Our internal region codes
 *   - Custom region names that legal complains about
 */
export class DisplayNames extends OriginalDisplayNames {
    /**
     * Tesla wrapper around [`Intl.DisplayNames()`](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DisplayNames)
     * Adds functionality to allow for
     *   - Our internal region codes
     *   - Custom region names that legal complains about
     *
     * @param locales A string with a BCP 47 language tag, or an array of such strings.
     *   For the general form and interpretation of the `locales` argument, see the [Intl](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl#locale_identification_and_negotiation)
     *   page.
     *
     * @param options An object for setting up a display name.
     *
     * [MDN](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DisplayNames/DisplayNames).
     */
    constructor(...superConstructorParams) {
        super(...superConstructorParams);
        this.requestedOptions = superConstructorParams[1] || {};
    }
    of(...args) {
        var _a, _b, _c;
        let result = (_c = (_b = (_a = this.getOverrideOrUndefinedForSuperRegion(...args)) !== null && _a !== void 0 ? _a : this.getOverrideOrUndefinedForOptions(...args)) !== null && _b !== void 0 ? _b : this.getOverrideOrUndefinedForOf(...args)) !== null && _c !== void 0 ? _c : super.of(...args);
        // Apply string replacements
        if (result) {
            for (const stringToFind in stringReplaceOverrides) {
                const replacement = stringReplaceOverrides[stringToFind];
                if (!!replacement) {
                    result = result.replace(stringToFind, replacement);
                }
            }
        }
        return result;
    }
    getOverrideOrUndefinedForSuperRegion(...args) {
        var _a;
        const [code] = args;
        const { locale } = this.resolvedOptions();
        const superRegion = superRegionDisplayNames[code];
        const { language } = new Intl.Locale(locale).maximize();
        if (superRegion) {
            const localized = (_a = superRegion[language]) !== null && _a !== void 0 ? _a : superRegion["en"];
            if (localized) {
                return localized;
            }
        }
        return undefined;
    }
    getOverrideOrUndefinedForOptions(...args) {
        const [code] = args;
        // Check if there are any overrides for the country
        const displayNameTypeToOptionsOverrides = optionsOverrides[code];
        if (displayNameTypeToOptionsOverrides == null) {
            return undefined;
        }
        // Check if there are overrides for this particular display type
        const resolvedOptions = this.resolvedOptions();
        const { locale, type } = resolvedOptions;
        const optionOverrides = displayNameTypeToOptionsOverrides[type];
        if (optionOverrides == null) {
            return undefined;
        }
        // See if any of the values defined in optionOverrides defined are null in
        // this.requestedOptions, if so then we need to override the original options
        // and recurse
        for (const overrideKeyAsString in optionOverrides) {
            const overrideKey = overrideKeyAsString;
            if (this.requestedOptions[overrideKey] == null) {
                const newFormatter = new Intl.DisplayNames(locale, Object.assign(Object.assign({}, resolvedOptions), optionOverrides));
                return newFormatter.of(...args);
            }
        }
        return undefined;
    }
    getOverrideOrUndefinedForOf(...args) {
        var _a, _b;
        const [code] = args;
        const { locale, type } = this.resolvedOptions();
        const maximized = new Intl.Locale(locale).maximize();
        const { language, region, script } = maximized;
        const keysToAttempt = [
            `${language}-${script}-${region}`,
            `${language}-${script}`,
            `${language}`,
        ];
        for (const keyToAttempt of keysToAttempt) {
            // Only type=language seems to be case insensitive
            const transformedCode = type !== "language" ? code : code.toLowerCase();
            const override = (_b = (_a = ofOverrides[keyToAttempt]) === null || _a === void 0 ? void 0 : _a[type]) === null || _b === void 0 ? void 0 : _b[transformedCode];
            if (override != null) {
                return override;
            }
        }
        return undefined;
    }
}
